import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Page } from "../../../components/Page";
import { TooltipLayer } from "./components/TooltipLayer";
import Spinner from "../../../components/Spinners";
import { ShowToast } from "../../../components/toast";
import { BackBar } from "./components/BackBar";
import { SectionCard } from "./components/SectionCard";
import { LoadError } from "./components/LoadError";
import { Chip } from "./components/Chip";
import { OutcomeLegend } from "./components/OutcomeSquare";
import { VerdictCounters } from "./components/VerdictCounters";
import { LockActionModal } from "./components/modals/LockActionModal";
import { useQueues, thKeys } from "./hooks/useTrackingHealth";
import { trackingHealth } from "../../../helpers/api/tracking-health";
import { fmtActor, fmtDate, fmtMoney } from "./format";
import {
  CONDITION_REVIEW_FLAG_DISPLAY,
  CONDITION_REVIEW_FLAG_EXPLANATION,
  HOTEL_STATE_DISPLAY,
  LOW_VOLUME_HINT,
  SILENCE_CAUSE_DISPLAY,
  UNDETERMINED_DISPLAY,
} from "./vocabulary";
import type { SilenceCause, QueuesPayload } from "./types";

const LIST_PREVIEW = 8;

export function Queues() {
  const queues = useQueues();
  const queryClient = useQueryClient();
  const [unlockTarget, setUnlockTarget] = useState<{
    customerId: string;
    hotelName: string;
  } | null>(null);

  /** Ending a silence from the queue — the same act as the incident page's
   *  "Return to cadence", so it invalidates the same caches. */
  const endSilence = useMutation({
    mutationFn: (vars: { customerId: string }) =>
      trackingHealth.clearSilence(vars.customerId),
    onSuccess: () => {
      ShowToast({
        type: "success",
        message: "Silence ended — reminder back on its ordinary clock",
      });
      queryClient.invalidateQueries({ queryKey: thKeys.queues });
      queryClient.invalidateQueries({ queryKey: ["th", "incident"] });
      queryClient.invalidateQueries({ queryKey: ["th", "hotels"] });
    },
    onError: (error: any) =>
      ShowToast({
        type: "error",
        message: error?.response?.data?.message ?? "Failed to end silence",
      }),
  });

  const unlock = useMutation({
    mutationFn: (vars: { customerId: string; reason: string }) =>
      trackingHealth.removeLock(vars.customerId, "validator", {
        reason: vars.reason,
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Validator lock removed" });
      queryClient.invalidateQueries({ queryKey: thKeys.queues });
      queryClient.invalidateQueries({ queryKey: thKeys.summary });
      setUnlockTarget(null);
    },
    onError: (error: any) =>
      ShowToast({
        type: "error",
        message: error?.response?.data?.message ?? "Failed to remove lock",
      }),
  });

  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <BackBar
              crumbs={[
                { label: "Tracking Health", to: "/tracking/health" },
                { label: "Advanced" },
              ]}
            />
            <h1 className="text-2xl font-semibold text-black mt-1">Advanced</h1>
            <div className="text-sm text-content-secondary mt-1">
              Standing lists live here — alerts fire only on events
            </div>
          </div>
        </div>

        {queues.isLoading && (
          <div className="flex justify-center py-16">
            <Spinner className="!text-brand-primary" />
          </div>
        )}
        {queues.isError && (
          <LoadError
            error={queues.error}
            label="the queues"
            onRetry={() => queues.refetch()}
          />
        )}

        {queues.data && (
          <>
            <Band title="Work owed" hint="clears when someone does it">
              <BackstopCard data={queues.data} />
              <SeedCohortCard
                data={queues.data}
                onUnlock={(customerId, hotelName) =>
                  setUnlockTarget({ customerId, hotelName })
                }
              />
              <ReconfirmCard data={queues.data} />
            </Band>

            <Band
              title="Standing dispositions"
              hint="a person put these in place — they stay until a person removes them"
            >
              <SilencedCard
                data={queues.data}
                pending={endSilence.isPending}
                onEnd={(customerId) => endSilence.mutate({ customerId })}
              />
              <LocksCard data={queues.data} />
              <ConditionsCard data={queues.data} />
              <ExcludedCard data={queues.data} />
      <OverridesCard data={queues.data} />
              <RaisedProofCard data={queues.data} />
            </Band>

            <Band
              title="Standing facts and counters"
              hint="nothing to action — read these for drift"
            >
              <NoReadableGa4Card data={queues.data} />
              <SilenceCausesCard data={queues.data} />
              <CaptureVerdictsCard data={queues.data} />
              <NotCheckableCard data={queues.data} />
            </Band>
          </>
        )}

        {unlockTarget && (
          <LockActionModal
            customerId={unlockTarget.customerId}
            lock="validator"
            action="remove"
            pending={unlock.isPending}
            onClose={() => setUnlockTarget(null)}
            onSubmit={(reason) =>
              unlock.mutate({ customerId: unlockTarget.customerId, reason })
            }
          />
        )}
      </div>
      <TooltipLayer />
    </Page>
  );
}

// ─── Column 1 cards ───────────────────────────────────────────────────────────

function HotelLink({ customerId, name }: { customerId: string; name: string }) {
  return (
    <Link
      to={`/tracking/health/hotels/${customerId}`}
      className="text-content-primary hover:text-brand-primary font-medium"
    >
      {name}
    </Link>
  );
}

function ExpandableRows<T>({
  rows,
  render,
}: {
  rows: T[];
  render: (row: T) => React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, LIST_PREVIEW);
  return (
    <>
      {visible.map((row, i) => (
        <div key={i}>{render(row)}</div>
      ))}
      {rows.length > LIST_PREVIEW && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover mt-2"
        >
          {expanded ? "Show fewer" : `+ ${rows.length - LIST_PREVIEW} more`}
        </button>
      )}
    </>
  );
}

/**
 * A standing list, with its length in the header.
 *
 * This page's question is "which of these has something in it right now", and
 * twelve equally-weighted cards answer it only by being read one at a time —
 * a card holding nothing looked exactly like one holding forty. So the count
 * leads, and an empty list recedes instead of competing for the same attention.
 */
function QueueCard({
  title,
  count,
  clearLabel = "none",
  children,
}: {
  title: string;
  count: number;
  /** What "empty" means for THIS list — an empty backstop queue is cleared
   *  work, an empty seed cohort is a pass that has not run yet. */
  clearLabel?: string;
  children: React.ReactNode;
}) {
  const empty = count === 0;
  return (
    <SectionCard
      title={title}
      className={empty ? "opacity-75 hover:opacity-100 transition-opacity" : ""}
      headerRight={
        empty ? (
          <span className="text-xs text-gray-400">{clearLabel}</span>
        ) : (
          <span className="text-xs font-semibold tabular-nums text-content-primary bg-[#F4F4F6] rounded px-2 py-0.5">
            {count}
          </span>
        )
      }
    >
      {children}
    </SectionCard>
  );
}

/**
 * The three kinds of thing this page holds, which is the one distinction its
 * two hand-assigned columns never encoded: what closes the item. Work owed
 * clears when somebody does it, a disposition stays until somebody removes it,
 * and a counter is never "done" at all — it is watched for drift.
 */
function Band({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6 last:mb-0">
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <h2 className="text-[11.5px] font-semibold uppercase tracking-wider text-gray-500">
          {title}
        </h2>
        <span className="text-[11.5px] text-gray-400">{hint}</span>
      </div>
      {/* No `items-start`: grid's default stretch is what makes both cards in
          a row as tall as the taller one, so a full list beside a cleared one
          leaves white card rather than a ragged hole in the page. The rest of
          the module's card grids (HotelDetail's especially) rely on the same
          default — keep them agreeing. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function BackstopCard({ data }: { data: QueuesPayload }) {
  const queue = data.goLiveBackstop;
  return (
    <QueueCard
      title="Go-live backstop queue"
      count={queue.hotels.length}
      clearLabel="clear"
    >
      <div className="text-xs text-gray-500 mb-2">
        awaiting first go-live run · limit {queue.dailyCap}/day · highest-spend
        first
      </div>
      {queue.hotels.length === 0 ? (
        <p className="text-sm text-gray-400">Queue is clear.</p>
      ) : (
        <ExpandableRows
          rows={queue.hotels}
          render={(hotel) => (
            <div className="flex items-center justify-between py-1.5 border-b border-[#E9E9EB] text-sm">
              <HotelLink customerId={hotel.customerId} name={hotel.hotelName} />
              <span className="text-gray-500">
                {fmtMoney(hotel.spend30d, hotel.currency)}
                <span className="text-gray-400">
                  {" "}
                  · queued since {fmtDate(hotel.owedSince)}
                </span>
              </span>
            </div>
          )}
        />
      )}
    </QueueCard>
  );
}

/**
 * Standing silences (ADR 0017). A silence is quiet, never hidden — this card
 * is what keeps it from becoming a suppression list nobody re-reads, so like
 * the seed cohort it renders an empty state rather than being omitted.
 *
 * Active silences only: one that expired or was broken by a new problem is
 * already back on the ordinary cadence and belongs to its incident's thread.
 */
function SilencedCard({
  data,
  onEnd,
  pending,
}: {
  data: QueuesPayload;
  onEnd: (customerId: string, hotelName: string) => void;
  pending: boolean;
}) {
  const rows = data.silenced ?? [];
  return (
    <QueueCard
      title="Silenced reminders"
      count={rows.length}
      clearLabel="none silenced"
    >
      <div className="text-xs text-gray-500 mb-2">
        reminder deferred · returns on its own
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">Nothing silenced.</p>
      ) : (
        <ExpandableRows
          rows={rows}
          render={(row) => (
            <div className="flex items-start justify-between gap-2 py-1.5 border-b border-[#E9E9EB] text-sm">
              <div className="min-w-0">
                <HotelLink
                  customerId={row.customerId}
                  name={row.hotelName ?? row.customerId}
                />
                <div className="text-xs text-gray-400">
                  {row.primaryIssue ?? "undiagnosed"}
                  {row.silence.note && <> · “{row.silence.note}”</>}
                </div>
              </div>
              <div className="text-right whitespace-nowrap">
                <div className="text-gray-500">
                  returns {fmtDate(row.silence.until)}
                  <span className="text-gray-400">
                    {" "}
                    · {row.silence.daysRemaining}d
                  </span>
                </div>
                <button
                  onClick={() =>
                    onEnd(row.customerId, row.hotelName ?? row.customerId)
                  }
                  disabled={pending}
                  className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover disabled:opacity-50"
                >
                  end now
                </button>
              </div>
            </div>
          )}
        />
      )}
    </QueueCard>
  );
}

function SeedCohortCard({
  data,
  onUnlock,
}: {
  data: QueuesPayload;
  onUnlock: (customerId: string, hotelName: string) => void;
}) {
  const cohort = data.deferredSeedCohort;
  const totalSpend = cohort.reduce((sum, row) => sum + (row.spend30d || 0), 0);
  return (
    <QueueCard
      title="Deferred seed cohort"
      count={cohort.length}
      clearLabel="not seeded yet"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-content-primary font-medium">
          Healthy at seed · {fmtMoney(totalSpend)}/mo
        </span>
        <Chip tone="purple">Validator-locked (auto-applied)</Chip>
      </div>
      <div className="text-xs text-gray-500 mb-2">
        deferred from the full-strength catalog · clears per hotel
      </div>
      {cohort.length === 0 ? (
        // Legitimately empty until the seeding pass runs — that emptiness is
        // itself true (§5.6).
        <p className="text-sm text-gray-400">
          Empty — the cohort is seeded by the rollout seeding pass.
        </p>
      ) : (
        <ExpandableRows
          rows={cohort}
          render={(row) => (
            <div className="flex items-center justify-between py-1.5 border-b border-[#E9E9EB] text-sm gap-2">
              <HotelLink customerId={row.customerId} name={row.hotelName} />
              <span className="flex items-center gap-2">
                <span className="text-gray-500">
                  {fmtMoney(row.spend30d, row.currency)}
                </span>
                <button
                  onClick={() => onUnlock(row.customerId, row.hotelName)}
                  className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover"
                >
                  unlock now
                </button>
              </span>
            </div>
          )}
        />
      )}
    </QueueCard>
  );
}

function LocksCard({ data }: { data: QueuesPayload }) {
  return (
    <QueueCard
      title="Locks"
      count={data.locks.length}
      clearLabel="none standing"
    >
      {data.locks.length === 0 ? (
        <p className="text-sm text-gray-400">No standing locks.</p>
      ) : (
        <ExpandableRows
          rows={data.locks}
          render={(lock) => (
            <div className="py-1.5 border-b border-[#E9E9EB] text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <HotelLink customerId={lock.customerId} name={lock.hotelName} />
                <Chip tone={lock.lock === "validator" ? "purple" : "neutral"}>
                  {lock.lock}
                </Chip>
                <Chip tone="neutral">{lock.auto ? "auto" : "manual"}</Chip>
                <span className="text-xs text-gray-400">
                  {fmtDate(lock.appliedAt)}
                  {lock.ageDays != null && ` · ${lock.ageDays}d`}
                </span>
              </div>
              {lock.reason && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {lock.reason}
                </div>
              )}
            </div>
          )}
        />
      )}
    </QueueCard>
  );
}

/**
 * The codification counter: which benign causes recur often enough to graduate
 * into a check, a pattern, or a threshold change. Its input moved from the
 * retired false-positive close onto the silence, where naming a cause is
 * OPTIONAL — so this counts the deferrals somebody explained, never all of
 * them, and the empty state says so rather than claiming a quiet fleet.
 */
function SilenceCausesCard({ data }: { data: QueuesPayload }) {
  const causes = data.counters.silenceCauses90d;
  const max = Math.max(1, ...causes.map((c) => c.count));
  return (
    <QueueCard
      title="Silence causes · 90d"
      count={causes.length}
      clearLabel="none named"
    >
      {causes.length === 0 ? (
        <p className="text-sm text-gray-400">
          No cause named on a silence in the window — naming one is optional.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {causes.map(({ cause, count }) => (
            <div key={cause} className="flex items-center gap-2">
              <span className="text-xs text-gray-600 w-44 shrink-0">
                {SILENCE_CAUSE_DISPLAY[cause as SilenceCause] ?? cause}
              </span>
              {/* Counts are neutral data, not warnings — muted teal (§5.6). */}
              <div className="flex-1 h-4 rounded-sm bg-[#F4F4F6]">
                <div
                  className="h-4 rounded-sm bg-brand-primary-light"
                  style={{ width: `${(count / max) * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-600 w-6 text-right">
                {count}
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        The codification feed — a recurring benign cause earning a check starts
        here.
      </p>
    </QueueCard>
  );
}

function ConditionsCard({ data }: { data: QueuesPayload }) {
  // Flagged first: a standing condition is a fact, but a FLAGGED one is the
  // queue item — an automated event asked for a human re-read, and only a
  // human clears it (conditions are never auto-retired). Oldest flag first
  // within that group, the same stalest-first ordering the worklist uses.
  const active = data.acceptedConditions
    .filter((c) => c.status === "active")
    .sort((a, b) => {
      if (!!a.reviewFlag !== !!b.reviewFlag) return a.reviewFlag ? -1 : 1;
      return (a.reviewFlaggedAt ?? "").localeCompare(b.reviewFlaggedAt ?? "");
    });
  return (
    <QueueCard
      title="Accepted conditions"
      count={active.length}
      clearLabel="none standing"
    >
      {active.length === 0 ? (
        <p className="text-sm text-gray-400">No standing conditions.</p>
      ) : (
        <ExpandableRows
          rows={active}
          render={(condition) => (
            <div className="py-1.5 border-b border-[#E9E9EB] text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <HotelLink
                  customerId={condition.customerId}
                  name={condition.hotelName ?? condition.customerId}
                />
                <Chip
                  tone={
                    condition.reasonCategory === "commercial"
                      ? "blue"
                      : "purple"
                  }
                >
                  {condition.reasonCategory}
                </Chip>
                <span className="text-gray-600">
                  {condition.scopeType === "rule"
                    ? `Rule ${condition.scopeId}`
                    : `P${condition.scopeId}`}
                </span>
                <span className="text-xs text-gray-400">
                  · {condition.ageDays}d
                </span>
                {condition.reviewFlag && (
                  <Chip
                    tone="orange"
                    title={
                      CONDITION_REVIEW_FLAG_EXPLANATION[condition.reviewFlag]
                    }
                  >
                    re-review ·{" "}
                    {CONDITION_REVIEW_FLAG_DISPLAY[condition.reviewFlag]}
                  </Chip>
                )}
              </div>
              {condition.exitCondition && (
                <div className="text-xs text-gray-500 mt-0.5">
                  exit: {condition.exitCondition}
                </div>
              )}
            </div>
          )}
        />
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        flagged for re-review on a route change or a re-enrollment — open the
        hotel to re-confirm or remove
      </p>
    </QueueCard>
  );
}

function ExcludedCard({ data }: { data: QueuesPayload }) {
  return (
    <QueueCard
      title="Excluded from Tracking Health"
      count={data.excluded.length}
      clearLabel="none"
    >
      {data.excluded.length === 0 ? (
        <p className="text-sm text-gray-400">
          Every enrolled hotel is being swept.
        </p>
      ) : (
        <ExpandableRows
          rows={data.excluded}
          render={(row) => (
            <div className="py-1.5 px-2 -mx-2 rounded border-b border-[#E9E9EB] text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <HotelLink customerId={row.customerId} name={row.hotelName} />
                <Chip
                  tone="neutral"
                  title={row.sentence}
                >
                  {row.reason === "crm-test" ? "test account" : "test-named"}
                </Chip>
                {row.frozenReportedStatus && (
                  <span className="text-gray-600">
                    frozen at {row.frozenReportedStatus}
                  </span>
                )}
                {row.frozenOpenIncidentId && (
                  <span
                    className="text-xs text-gray-500 underline decoration-dotted cursor-help"
                    data-tip="Its incident is still open and frozen — it resumes where it left off if the hotel is readmitted."
                  >
                    incident held
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  {row.ageDays != null && `· ${row.ageDays}d`}
                </span>
              </div>
            </div>
          )}
        />
      )}
      {/* An exclusion is quiet, never hidden. A list nobody re-reads is how a
          freeze turns into a hotel forgotten for a year, so this card stands
          whether or not anything is in it. */}
      <p className="text-[11px] text-gray-400 mt-2">
        Frozen out of every sweep — no rules, no Validator, no alerts. Nothing
        underneath is thrown away. Un-mark the customer as a test account in the
        CRM and the hotel returns on the next daily sync.
      </p>
    </QueueCard>
  );
}

function OverridesCard({ data }: { data: QueuesPayload }) {
  return (
    <QueueCard
      title="Admin overrides"
      count={data.overrides.length}
      clearLabel="none standing"
    >
      {data.overrides.length === 0 ? (
        <p className="text-sm text-gray-400">No standing overrides.</p>
      ) : (
        <ExpandableRows
          rows={data.overrides}
          render={(override) => (
            <div className="py-1.5 px-2 -mx-2 rounded bg-warning-light/40 border-b border-[#E9E9EB] text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <HotelLink
                  customerId={override.customerId}
                  name={override.hotelName}
                />
                <span className="text-gray-600">
                  reported {HOTEL_STATE_DISPLAY[override.reported]} / computed{" "}
                  {override.computed
                    ? HOTEL_STATE_DISPLAY[override.computed]
                    : UNDETERMINED_DISPLAY.toLowerCase()}
                </span>
                {/* The category used to apply both locks, and this chip named
                    them so a standing disposition was not discovered on the
                    hotel page. ADR 0040 withdrew the locks, so the chip now
                    names what the category actually does: the booking rules
                    stand down, nothing is locked. */}
                {override.reported === "low-volume" && (
                  <Chip tone="neutral" title={LOW_VOLUME_HINT}>
                    rules stand down
                  </Chip>
                )}
                <span className="text-xs text-gray-400">
                  {override.ageDays != null && `· ${override.ageDays}d`}
                </span>
                {override.note && (
                  <span
                    className="text-xs text-gray-500 underline decoration-dotted cursor-help"
                    data-tip={override.note}
                  >
                    note attached
                  </span>
                )}
              </div>
            </div>
          )}
        />
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        Listed until cleared — nothing expires.
      </p>
    </QueueCard>
  );
}

function RaisedProofCard({ data }: { data: QueuesPayload }) {
  return (
    <QueueCard
      title="Raised proof requirements"
      count={data.raisedProofRequirements.length}
      clearLabel="none raised"
    >
      {data.raisedProofRequirements.length === 0 ? (
        <p className="text-sm text-gray-400">None raised.</p>
      ) : (
        <ExpandableRows
          rows={data.raisedProofRequirements}
          render={(row) => (
            <div className="py-1.5 border-b border-[#E9E9EB] text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <HotelLink customerId={row.customerId} name={row.hotelName} />
                <span className="text-gray-600">
                  {row.required} bookings required
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                raised {fmtDate(row.setAt)} by {fmtActor(row.setBy) || "?"}
                {row.reason && <> — {row.reason}</>}
              </div>
            </div>
          )}
        />
      )}
    </QueueCard>
  );
}

function ReconfirmCard({ data }: { data: QueuesPayload }) {
  const rows = data.reconfirmationQueue;
  return (
    <QueueCard
      title="Re-confirmation queue"
      count={rows.length}
      clearLabel="clear"
    >
      <div className="text-xs text-gray-500 mb-1">
        returned accounts — dispositions flagged for re-confirmation
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">Nothing to re-confirm.</p>
      ) : (
        <ExpandableRows
          rows={rows}
          render={(row) => (
            <div className="py-1.5 border-b border-[#E9E9EB] text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <HotelLink customerId={row.customerId} name={row.hotelName} />
                <span className="text-xs text-gray-400">
                  flagged {fmtDate(row.flaggedAt)}
                </span>
              </div>
              {row.evidence && (
                <div className="text-xs text-gray-500 mt-0.5 break-words">
                  {summarizeDispositions(row.evidence)}
                </div>
              )}
            </div>
          )}
        />
      )}
    </QueueCard>
  );
}

/** The evidence blob names the surviving dispositions; shape untyped —
 *  render its keys/values compactly rather than dropping it. */
function summarizeDispositions(evidence: Record<string, unknown>): string {
  try {
    return Object.entries(evidence)
      .map(([key, value]) =>
        typeof value === "string" || typeof value === "number"
          ? `${key}: ${value}`
          : key,
      )
      .join(" · ");
  } catch {
    return "";
  }
}

function CaptureVerdictsCard({ data }: { data: QueuesPayload }) {
  const counters = data.counters;
  return (
    <SectionCard title="Support-capture verdicts · 30d">
      <VerdictCounters counts={counters.captureVerdicts30d} />
      {/* Backend issue 42: the deployment census is back — from the payload
          layer's served-gtm.js observation, summed over latest runs. The
          `observed` denominator is honest: a hotel whose latest run never
          read the payload is unknown, not absent. */}
      <p className="text-xs text-gray-500 mt-3">
        support tag deployed in {counters.supportTag.present} of{" "}
        {counters.supportTag.observed} observed containers (
        {counters.gtmTrackedHotels} GTM-tracked)
      </p>
      {/* `k` counts containers observed capturing — the only fact the
          capture store can witness. */}
      <p className="text-xs text-gray-500 mt-1">
        captures observed from {counters.containersWithCaptures} of{" "}
        {counters.gtmTrackedHotels} GTM-tracked containers
      </p>
      <p className="text-[11px] text-gray-400">
        {counters.containersWithCaptures} counts containers we have seen fire —
        deployment comes from the served-payload observation above
      </p>
    </SectionCard>
  );
}

function NoReadableGa4Card({ data }: { data: QueuesPayload }) {
  return (
    <QueueCard
      title="No readable GA4"
      count={data.noReadableGa4.length}
      clearLabel="all readable"
    >
      {data.noReadableGa4.length === 0 ? (
        <p className="text-sm text-gray-400">
          Every judged hotel has readable GA4.
        </p>
      ) : (
        <ExpandableRows
          rows={data.noReadableGa4}
          render={(row) => (
            <div className="flex items-center justify-between py-1.5 border-b border-[#E9E9EB] text-sm">
              <HotelLink customerId={row.customerId} name={row.hotelName} />
              <Chip tone="neutral">
                {row.trackingMode === "gtm"
                  ? "GTM-tracked"
                  : row.trackingMode === "ga4-only"
                    ? "GA4-only"
                    : "no GA4 / no GTM"}
              </Chip>
            </div>
          )}
        />
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        Rule 3 reads insufficient on these by construction — not a failure
      </p>
    </QueueCard>
  );
}

function NotCheckableCard({ data }: { data: QueuesPayload }) {
  const level = data.counters.notCheckable;
  return (
    <SectionCard title="Standing not-checkable level">
      <OutcomeLegend counts={level.outcomes} />
      <p className="text-[11px] text-gray-400 mt-2">
        Summed over each hotel's latest run ({level.hotelsWithRuns} hotels with
        runs). The level, not the jump — a sudden rise is our egress, tokens or
        routing, and module alerts watch for it; this card is where a slow drift
        has somewhere to be noticed.
      </p>
    </SectionCard>
  );
}
